// src/pages/ProjectsPage.jsx

import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import CryptoJS from "crypto-js";
import SEO from "../components/common/SEO";

// Import components
import ErrorAlert from "../components/Projects/ErrorAlert";
import RateLimitWarning from "../components/Projects/RateLimitWarning";
import GitHubOverview from "../components/Projects/GitHubOverview";
import SearchFilter from "../components/Projects/SearchFilter";
import FeaturedProjects from "../components/Projects/FeaturedProjects";
import ProjectsGrid from "../components/Projects/ProjectsGrid";
import LoadMoreButton from "../components/Projects/LoadMoreButton";

// Define featured project names manually
const featuredProjectsNames = ["vxdf", "vulndroid", "learning-linux"];

// Encrypted GitHub token
const encryptedToken =
  "U2FsdGVkX18/+bo3T8jAvHjtVa9TP6uho5bf/6Up35gu6LZRNRbW7N62YmHHkh8vLR2gELqvZWEXoF4izNzibw==";

// Decrypt the token
const decryptToken = () => {
  const encryptionKey = "zsOGRHXC1LtuyJTjlyhs1qIF09c6obZn";
  const bytes = CryptoJS.AES.decrypt(encryptedToken, encryptionKey);
  const decryptedToken = bytes.toString(CryptoJS.enc.Utf8);
  return decryptedToken;
};

const ProjectsPage = () => {
  const githubToken = decryptToken();

  // State variables
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileStats, setProfileStats] = useState({
    followers: 0,
    publicRepos: 0,
    totalStars: 0,
  });
  const [contributionScore, setContributionScore] = useState(0);
  const [contributionGrade, setContributionGrade] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("all");
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [rateLimit, setRateLimit] = useState({ remaining: null, reset: null });
  const [isBackoff, setIsBackoff] = useState(false);

  const cache = useRef({
    repos: {},
    profileStats: null,
  });

  const GITHUB_API_BASE = "https://api.github.com";
  const BACKOFF_THRESHOLD = 200;
  const BACKOFF_DELAY = 30000; // 30 seconds

  // Update rate limit state
  const updateRateLimit = useCallback((headers) => {
    const remaining = parseInt(headers.get("X-RateLimit-Remaining"));
    const reset = parseInt(headers.get("X-RateLimit-Reset"));
    setRateLimit({ remaining, reset });
  }, []);

  // Fetch repositories and their languages
  const fetchReposAndLanguages = useCallback(
    async (pageNumber = 1, perPage = 30) => {
      try {
        const cacheKey = `page_${pageNumber}_perPage_${perPage}`;
        // Check if data is in cache
        if (cache.current.repos[cacheKey]) {
          return cache.current.repos[cacheKey];
        }

        const reposResponse = await fetch(
          `${GITHUB_API_BASE}/user/repos?visibility=public&affiliation=owner&per_page=${perPage}&page=${pageNumber}`,
          {
            headers: {
              Authorization: `token ${githubToken}`,
              Accept: "application/vnd.github.v3+json",
            },
          },
        );

        // Update rate limit based on response headers
        updateRateLimit(reposResponse.headers);

        if (!reposResponse.ok) {
          const errorData = await reposResponse.json();
          throw new Error(
            `Failed to fetch repositories: ${reposResponse.status} ${reposResponse.statusText} - ${errorData.message}`,
          );
        }

        const reposData = await reposResponse.json();

        // Fetch languages for each repo
        const reposWithLanguages = await Promise.all(
          reposData.map(async (repo) => {
            try {
              const languagesResponse = await fetch(repo.languages_url, {
                headers: {
                  Authorization: `token ${githubToken}`,
                  Accept: "application/vnd.github.v3+json",
                },
              });

              // Update rate limit based on response headers
              updateRateLimit(languagesResponse.headers);

              if (!languagesResponse.ok) {
                const langErrorData = await languagesResponse.json();
                throw new Error(
                  `Failed to fetch languages for repo "${repo.name}": ${languagesResponse.status} ${languagesResponse.statusText} - ${langErrorData.message}`,
                );
              }

              const languages = await languagesResponse.json();
              return { ...repo, languages };
            } catch (langError) {
              console.error(langError);
              return { ...repo, languages: {} }; // Fallback to empty languages
            }
          }),
        );

        // Store in cache
        cache.current.repos[cacheKey] = reposWithLanguages;

        return reposWithLanguages;
      } catch (err) {
        console.error(err);
        setError(err.message);
        return [];
      }
    },
    [githubToken, updateRateLimit],
  );

  // Fetch profile stats
  const fetchProfileStats = useCallback(async () => {
    try {
      // Check if profile stats are cached
      if (cache.current.profileStats) {
        return cache.current.profileStats;
      }

      // Fetch user profile
      const userResponse = await fetch(`${GITHUB_API_BASE}/user`, {
        headers: {
          Authorization: `token ${githubToken}`,
          Accept: "application/vnd.github.v3+json",
        },
      });

      // Update rate limit based on response headers
      updateRateLimit(userResponse.headers);

      if (!userResponse.ok) {
        const userErrorData = await userResponse.json();
        throw new Error(
          `Failed to fetch user profile: ${userResponse.status} ${userResponse.statusText} - ${userErrorData.message}`,
        );
      }

      const userData = await userResponse.json();

      // Fetch your repositories
      const reposResponse = await fetch(
        `${GITHUB_API_BASE}/user/repos?visibility=public&affiliation=owner&per_page=100`,
        {
          headers: {
            Authorization: `token ${githubToken}`,
            Accept: "application/vnd.github.v3+json",
          },
        },
      );

      // Update rate limit based on response headers
      updateRateLimit(reposResponse.headers);

      if (!reposResponse.ok) {
        const reposErrorData = await reposResponse.json();
        throw new Error(
          `Failed to fetch repositories: ${reposResponse.status} ${reposResponse.statusText} - ${reposErrorData.message}`,
        );
      }

      const reposData = await reposResponse.json();

      // Calculate total stars
      const totalStars = reposData.reduce(
        (sum, repo) => sum + repo.stargazers_count,
        0,
      );

      const stats = {
        followers: userData.followers,
        publicRepos: userData.public_repos,
        totalStars: totalStars,
      };

      // Cache the profile stats
      cache.current.profileStats = stats;

      return stats;
    } catch (err) {
      console.error(err);
      setError(err.message);
      return { followers: 0, publicRepos: 0, totalStars: 0 };
    }
  }, [githubToken, updateRateLimit]);

  // Calculate contribution score
  const calculateContributionScore = useCallback((stats) => {
    const { publicRepos, totalStars, followers } = stats;
    const score = Math.min(
      publicRepos * 2 + totalStars * 1.5 + followers * 1,
      100,
    );
    return Math.round(score);
  }, []);

  // Get grade from score
  const getGradeFromScore = useCallback((score) => {
    if (score >= 90) return "A+";
    if (score >= 85) return "A";
    if (score >= 80) return "A-";
    if (score >= 75) return "B+";
    if (score >= 70) return "B";
    if (score >= 65) return "B-";
    if (score >= 60) return "C+";
    if (score >= 55) return "C";
    if (score >= 50) return "C-";
    if (score >= 45) return "D+";
    if (score >= 40) return "D";
    if (score >= 35) return "D-";
    return "F";
  }, []);

  // Load data with caching and rate limit checks
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const repositories = await fetchReposAndLanguages(page, 30); // increased perPage to 30

        // Special fetch for VXDF if not already loaded
        if (page === 1) {
          const vxdfExists = repositories.some(
            (repo) => repo.name.toLowerCase() === "vxdf",
          );
          if (!vxdfExists) {
            try {
              // Try to fetch VXDF repository specifically
              const vxdfResponse = await fetch(
                `${GITHUB_API_BASE}/repos/mihir-shah99/vxdf`,
                {
                  headers: {
                    Authorization: `token ${githubToken}`,
                    Accept: "application/vnd.github.v3+json",
                  },
                },
              );

              if (vxdfResponse.ok) {
                const vxdfRepo = await vxdfResponse.json();
                // Fetch languages for VXDF
                const languagesResponse = await fetch(vxdfRepo.languages_url, {
                  headers: {
                    Authorization: `token ${githubToken}`,
                    Accept: "application/vnd.github.v3+json",
                  },
                });

                if (languagesResponse.ok) {
                  const languages = await languagesResponse.json();
                  repositories.unshift({ ...vxdfRepo, languages }); // Add to beginning
                }
              }
            } catch (err) {
              console.error("Error fetching VXDF specifically:", err);
            }
          }
        }

        // Filter out duplicate repositories based on id
        setRepos((prevRepos) => {
          const existingRepoIds = new Set(prevRepos.map((repo) => repo.id));
          const newRepos = repositories.filter(
            (repo) => !existingRepoIds.has(repo.id),
          );
          return [...prevRepos, ...newRepos];
        });

        // Extract unique languages from all repositories
        const languages = new Set();
        repositories.forEach((repo) => {
          if (repo.languages) {
            Object.keys(repo.languages).forEach((lang) => languages.add(lang));
          }
        });

        setAvailableLanguages((prevLanguages) => {
          const updatedLanguages = new Set([...prevLanguages, ...languages]);
          return Array.from(updatedLanguages).sort(); // Sort alphabetically
        });

        if (repositories.length < 30) {
          setHasMore(false); // No more repos to load
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchStats = async () => {
      const stats = await fetchProfileStats();
      setProfileStats(stats);

      // Calculate Contribution Score after fetching stats
      const score = calculateContributionScore(stats);
      setContributionScore(score);

      // Calculate and set the Contribution Grade
      const grade = getGradeFromScore(score);
      setContributionGrade(grade);
    };

    // Before fetching data, check if rate limit is exceeded
    if (rateLimit.remaining === 0) {
      const now = Date.now();
      if (rateLimit.reset && now < rateLimit.reset) {
        const timeUntilResetMs = rateLimit.reset - now;
        const minutes = Math.floor(timeUntilResetMs / 60000);
        const seconds = Math.floor((timeUntilResetMs % 60000) / 1000);
        setError(
          `GitHub API rate limit exceeded. Please try again in ${minutes}m ${seconds}s.`,
        );
        setLoading(false);
        return;
      } else {
        // Reset the rate limit if the reset time has passed
        setRateLimit((prev) => ({ ...prev, remaining: prev.limit }));
      }
    }

    fetchStats();
    fetchData();
  }, [
    page,
    rateLimit.remaining,
    rateLimit.reset,
    fetchReposAndLanguages,
    fetchProfileStats,
    calculateContributionScore,
    getGradeFromScore,
    githubToken,
    GITHUB_API_BASE,
  ]);

  // Handle search input
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value.toLowerCase());
  }, []);

  // Handle language filter
  const handleLanguageChange = useCallback((e) => {
    setSelectedLanguage(e.target.value);
  }, []);

  // Load more repos
  const loadMoreRepos = useCallback(() => {
    if (hasMore && !loading && rateLimit.remaining > 0 && !isBackoff) {
      if (rateLimit.remaining <= BACKOFF_THRESHOLD) {
        setIsBackoff(true);
        // Inform the user
        setError(
          `Approaching GitHub API rate limit. Delaying further requests for ${
            BACKOFF_DELAY / 1000
          } seconds.`,
        );
        // Set a timeout to remove backoff after delay
        setTimeout(() => {
          setIsBackoff(false);
          setError(null); // Clear the error message
          setPage((prevPage) => prevPage + 1);
        }, BACKOFF_DELAY);
      } else {
        setPage((prevPage) => prevPage + 1);
      }
    }
  }, [hasMore, loading, rateLimit.remaining, isBackoff]);

  // Filtered repositories
  const filteredRepos = useMemo(() => {
    return repos.filter((repo) => {
      const matchesSearch =
        repo.name.toLowerCase().includes(searchQuery) ||
        (repo.description &&
          repo.description.toLowerCase().includes(searchQuery));
      const matchesLanguage =
        selectedLanguage === "all" ||
        (repo.languages && repo.languages[selectedLanguage]);
      return matchesSearch && matchesLanguage;
    });
  }, [repos, searchQuery, selectedLanguage]);

  // Featured and regular repos
  const featuredRepos = useMemo(() => {
    const featured = filteredRepos.filter((repo) =>
      featuredProjectsNames.includes(repo.name.toLowerCase()),
    );
    // Sort featured repos according to the order in featuredProjectsNames
    return featured.sort((a, b) => {
      const indexA = featuredProjectsNames.indexOf(a.name.toLowerCase());
      const indexB = featuredProjectsNames.indexOf(b.name.toLowerCase());
      return indexA - indexB;
    });
  }, [filteredRepos]);

  const regularRepos = useMemo(() => {
    return filteredRepos.filter(
      (repo) => !featuredProjectsNames.includes(repo.name.toLowerCase()),
    );
  }, [filteredRepos]);

  // Time until rate limit reset
  const timeUntilReset = useMemo(() => {
    if (!rateLimit.reset) return null;
    const now = Date.now();
    const difference = rateLimit.reset - now;
    if (difference <= 0) return null;
    const minutes = Math.floor(difference / 60000);
    const seconds = Math.floor((difference % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }, [rateLimit.reset]);

  // Slider settings
  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: false,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      accessibility: true,
    }),
    [],
  );

  const projectsStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Mihir Shah - Projects & Open Source Contributions",
    "description": "Explore open source security projects, OWASP VXDF contributions, and cybersecurity tools developed by Mihir Shah.",
    "url": "https://mihirshah.tech/projects",
    "author": {
      "@type": "Person",
      "name": "Mihir Shah",
      "url": "https://mihirshah.tech"
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": "Security Projects",
      "description": "Open source security tools and OWASP contributions",
      "itemListElement": [
        {
          "@type": "SoftwareApplication",
          "name": "OWASP VXDF",
          "description": "Validated Exploitable Data Flow standard for vulnerability management",
          "url": "https://github.com/OWASP/VXDF",
          "applicationCategory": "Security Standard",
          "author": {
            "@type": "Person",
            "name": "Mihir Shah"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Projects & Open Source Contributions - Mihir Shah"
        description="Explore innovative security projects including OWASP VXDF, vulnerability management tools, and cloud security solutions. Open source contributions by cybersecurity expert Mihir Shah."
        keywords="Mihir Shah projects, OWASP VXDF, open source security, GitHub projects, vulnerability management, cloud security tools, cybersecurity projects, security standards"
        url="https://mihirshah.tech/projects"
        type="website"
        section="Projects"
        tags={["Projects", "Open Source", "Security Tools", "OWASP VXDF"]}
        structuredData={projectsStructuredData}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto py-20 px-4">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-vxdf-primary to-vxdf-accent bg-clip-text text-transparent">
              Projects & Contributions
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Open source projects, security tools, and contributions to the
              cybersecurity community
            </p>
          </div>

          {/* Error Messages */}
          <ErrorAlert error={error} />

          {/* Rate Limit Warning */}
          <RateLimitWarning
            rateLimit={rateLimit}
            timeUntilReset={timeUntilReset}
          />

          {/* GitHub Overview Section */}
          <GitHubOverview
            profileStats={profileStats}
            contributionScore={contributionScore}
            contributionGrade={contributionGrade}
          />

          {/* Search and Filter Section */}
          <SearchFilter
            searchQuery={searchQuery}
            handleSearchChange={handleSearchChange}
            selectedLanguage={selectedLanguage}
            handleLanguageChange={handleLanguageChange}
            availableLanguages={availableLanguages}
          />

          {loading && repos.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-vxdf-accent"></div>
            </div>
          ) : (
            <>
              {/* Featured Projects Section */}
              <FeaturedProjects
                featuredRepos={featuredRepos}
                sliderSettings={sliderSettings}
              />

              {/* Regular Projects Section */}
              <h2 className="text-3xl font-bold text-center mb-8 text-white">
                Other Projects
              </h2>
              <ProjectsGrid
                repos={regularRepos}
                sliderSettings={sliderSettings}
              />

              {/* Load More Button */}
              <LoadMoreButton
                loading={loading}
                hasMore={hasMore}
                rateLimit={rateLimit}
                isBackoff={isBackoff}
                loadMoreRepos={loadMoreRepos}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(ProjectsPage);
